import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  MessageCircle, Search, AlertCircle, CheckCircle, 
  Package, Truck, MapPin, Clock, Calendar, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { apiService } from '@/services/api';
import { CONTACT_INFO } from '@/data/mockData';

const STATUS_CONFIG = {
  pending: {
    label: 'Pending',
    color: 'bg-yellow-100 text-yellow-800',
    icon: Clock,
    description: 'Your booking is being processed'
  },
  confirmed: {
    label: 'Confirmed',
    color: 'bg-blue-100 text-blue-800',
    icon: CheckCircle,
    description: 'Booking confirmed, awaiting pickup'
  },
  assigned: {
    label: 'Driver Assigned',
    color: 'bg-purple-100 text-purple-800',
    icon: Truck,
    description: 'A driver has been assigned to your delivery'
  },
  en_route_pickup: {
    label: 'En Route to Pickup',
    color: 'bg-orange-100 text-orange-800',
    icon: MapPin,
    description: 'Driver is on the way to collect your items'
  },
  picked_up: {
    label: 'Items Collected',
    color: 'bg-indigo-100 text-indigo-800',
    icon: Package,
    description: 'Your items have been collected'
  },
  in_transit: {
    label: 'In Transit',
    color: 'bg-cyan-100 text-cyan-800',
    icon: Truck,
    description: 'Your items are on the way to the destination'
  },
  delivered: {
    label: 'Delivered',
    color: 'bg-green-100 text-green-800',
    icon: CheckCircle,
    description: 'Your items have been delivered successfully'
  },
  cancelled: {
    label: 'Cancelled',
    color: 'bg-red-100 text-red-800',
    icon: AlertCircle,
    description: 'This booking has been cancelled'
  }
};

const Track = () => {
  const [searchParams] = useSearchParams();
  const [trackingRef, setTrackingRef] = useState(searchParams.get('ref') || '');
  const [trackingResult, setTrackingResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleTrack = useCallback(async (e, refOverride = null) => {
    if (e) e.preventDefault();
    const ref = refOverride || trackingRef;
    
    if (!ref.trim()) {
      toast.error('Please enter a booking reference');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.trackBooking(ref.trim());
      setTrackingResult(response);
    } catch (err) {
      setError('Booking not found. Please check your reference number or contact us on WhatsApp.');
      setTrackingResult(null);
    } finally {
      setIsLoading(false);
    }
  }, [trackingRef]);

  // Auto-search if ref is in URL
  useEffect(() => {
    const refFromUrl = searchParams.get('ref');
    if (refFromUrl) {
      setTrackingRef(refFromUrl);
      handleTrack(null, refFromUrl);
    }
  }, [searchParams, handleTrack]);

  const getStatusConfig = (status) => {
    return STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const whatsappTrack = `https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent(`Hi Rocket Van Express, I'd like to track my delivery. Reference: ${trackingRef}`)}`;

  const supportOptions = [
    {
      title: 'Report an Issue',
      description: 'Something wrong with your delivery?',
      action: 'WhatsApp Support',
      icon: AlertCircle,
      color: 'text-red-600',
      message: 'Hi Rocket Van Express, I need to report an issue with my delivery.'
    },
    {
      title: 'Reschedule Delivery',
      description: 'Need to change your pickup/delivery time?',
      action: 'Request Reschedule',
      icon: Calendar,
      color: 'text-[#039fff]',
      message: 'Hi Rocket Van Express, I need to reschedule my delivery.'
    },
    {
      title: 'Update Details',
      description: 'Change address or contact information?',
      action: 'Update Booking',
      icon: RefreshCw,
      color: 'text-[#fb6402]',
      message: 'Hi Rocket Van Express, I need to update my booking details.'
    }
  ];

  return (
    <div className="min-h-screen" data-testid="track-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#001A47] via-[#002B6B] to-[#001A47] text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Search className="h-16 w-16 text-[#039fff] mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Track Your Delivery</h1>
            <p className="text-xl text-gray-300 mb-8">
              Real-time updates delivered straight to your WhatsApp
            </p>
          </div>
        </div>
      </section>

      {/* Tracking Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <Tabs defaultValue="track" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-8">
                <TabsTrigger value="track" data-testid="track-tab">Track Order</TabsTrigger>
                <TabsTrigger value="whatsapp" data-testid="whatsapp-tab">Track via WhatsApp</TabsTrigger>
              </TabsList>

              <TabsContent value="track">
                <Card>
                  <CardContent className="p-6 md:p-8">
                    <form onSubmit={handleTrack} className="space-y-4">
                      <div>
                        <Label htmlFor="tracking-ref">Booking Reference</Label>
                        <Input
                          id="tracking-ref"
                          data-testid="tracking-input"
                          placeholder="e.g., MZG-12345 or DLV-abc123"
                          value={trackingRef}
                          onChange={(e) => setTrackingRef(e.target.value.toUpperCase())}
                          className="uppercase"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          You received this reference via WhatsApp when you booked
                        </p>
                      </div>
                      <Button 
                        type="submit" 
                        className="w-full bg-[#fb6402] hover:bg-[#e55a02]"
                        disabled={isLoading}
                        data-testid="track-btn"
                      >
                        {isLoading ? (
                          <>
                            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                            Searching...
                          </>
                        ) : (
                          <>
                            <Search className="mr-2 h-4 w-4" />
                            Track My Order
                          </>
                        )}
                      </Button>
                    </form>

                    {/* Error State */}
                    {error && (
                      <div className="mt-8 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start space-x-3">
                          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-red-700 text-sm">{error}</p>
                            <a 
                              href={whatsappTrack} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-sm text-[#039fff] hover:underline mt-2"
                            >
                              <MessageCircle className="h-4 w-4 mr-1" />
                              Get help on WhatsApp
                            </a>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Tracking Result */}
                    {trackingResult && (
                      <div className="mt-8 pt-8 border-t" data-testid="tracking-result">
                        {/* Status Header */}
                        <div className="flex items-center justify-between mb-6">
                          <div>
                            <p className="text-sm text-gray-600">Booking Reference</p>
                            <p className="text-lg font-bold text-[#001A47]">{trackingResult.booking_ref}</p>
                          </div>
                          <Badge className={getStatusConfig(trackingResult.status).color}>
                            {getStatusConfig(trackingResult.status).label}
                          </Badge>
                        </div>

                        {/* Booking Details */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Service Type</p>
                            <p className="font-medium text-[#001A47] capitalize">{trackingResult.service_type?.replace('-', ' ') || 'Delivery'}</p>
                          </div>
                          {trackingResult.scheduled_datetime && (
                            <div>
                              <p className="text-xs text-gray-500 uppercase">Scheduled</p>
                              <p className="font-medium text-[#001A47]">{formatDate(trackingResult.scheduled_datetime)}</p>
                            </div>
                          )}
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Pickup</p>
                            <p className="font-medium text-[#001A47]">{trackingResult.pickup_address || 'Not specified'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase">Drop-off</p>
                            <p className="font-medium text-[#001A47]">{trackingResult.dropoff_address || 'Not specified'}</p>
                          </div>
                        </div>

                        {/* Status Description */}
                        <div className="mb-6 p-4 bg-[#039fff]/5 rounded-lg border border-[#039fff]/20">
                          <div className="flex items-center space-x-3">
                            {React.createElement(getStatusConfig(trackingResult.status).icon, {
                              className: "h-6 w-6 text-[#039fff]"
                            })}
                            <div>
                              <p className="font-semibold text-[#001A47]">{getStatusConfig(trackingResult.status).label}</p>
                              <p className="text-sm text-gray-600">{getStatusConfig(trackingResult.status).description}</p>
                            </div>
                          </div>
                        </div>

                        {/* Timeline */}
                        {trackingResult.updates && trackingResult.updates.length > 0 && (
                          <div className="mb-6">
                            <h4 className="font-semibold text-[#001A47] mb-4">Delivery Updates</h4>
                            <div className="space-y-4">
                              {trackingResult.updates.map((update, index) => (
                                <div key={`${update.timestamp}-${index}`} className="flex items-start space-x-4">
                                  <div className={`rounded-full p-2 flex-shrink-0 ${index === 0 ? 'bg-[#039fff]' : 'bg-gray-300'}`}>
                                    <CheckCircle className={`h-4 w-4 ${index === 0 ? 'text-white' : 'text-gray-600'}`} />
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-[#001A47]">{update.message}</p>
                                    <p className="text-xs text-gray-500">
                                      {formatDate(update.timestamp)}
                                      {update.location && ` • ${update.location}`}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* WhatsApp CTA */}
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-700 text-center mb-3">
                            Need real-time updates? Get notifications straight to your WhatsApp
                          </p>
                          <a href={whatsappTrack} target="_blank" rel="noopener noreferrer" className="block">
                            <Button className="w-full bg-[#25D366] hover:bg-[#22c55e]">
                              <MessageCircle className="mr-2 h-4 w-4" />
                              Get Updates on WhatsApp
                            </Button>
                          </a>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="whatsapp">
                <Card>
                  <CardContent className="p-6 md:p-8 text-center">
                    <MessageCircle className="h-16 w-16 text-[#25D366] mx-auto mb-6" />
                    <h3 className="text-2xl font-bold text-[#001A47] mb-4">Track on WhatsApp</h3>
                    <p className="text-gray-600 mb-6">
                      Get real-time tracking updates delivered straight to WhatsApp. Just send us your booking reference.
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg mb-6">
                      <p className="text-sm font-medium text-gray-700 mb-2">Your Booking Reference:</p>
                      <Input
                        placeholder="MZG-12345"
                        value={trackingRef}
                        onChange={(e) => setTrackingRef(e.target.value.toUpperCase())}
                        className="mb-3 uppercase"
                      />
                    </div>
                    <a href={whatsappTrack} target="_blank" rel="noopener noreferrer">
                      <Button size="lg" className="bg-[#25D366] hover:bg-[#22c55e] text-white w-full">
                        <MessageCircle className="mr-2 h-5 w-5" />
                        Track on WhatsApp
                      </Button>
                    </a>
                    
                    <div className="mt-6 pt-6 border-t">
                      <p className="text-sm text-gray-500">
                        Don&apos;t have a reference? No problem - just tell us your name and pickup address.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </section>

      {/* Support Options */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#001A47] mb-4">Need Help?</h2>
            <p className="text-lg text-gray-600">We&apos;re here to assist with any issues</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {supportOptions.map((option, index) => {
              const Icon = option.icon;
              return (
                <Card key={option.title} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <Icon className={`h-12 w-12 ${option.color} mx-auto mb-4`} />
                    <h3 className="font-bold text-[#001A47] mb-2">{option.title}</h3>
                    <p className="text-sm text-gray-600 mb-4">{option.description}</p>
                    <a 
                      href={`https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent(option.message)}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <Button variant="outline" className="w-full">
                        <MessageCircle className="mr-2 h-4 w-4" />
                        {option.action}
                      </Button>
                    </a>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How Tracking Works */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#001A47] mb-4">How Our Tracking Works</h2>
              <p className="text-lg text-gray-600">Stay informed every step of the way</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { step: 1, title: 'Enter Reference', description: 'Use the booking reference from your WhatsApp confirmation', icon: Search },
                { step: 2, title: 'See Live Status', description: 'View real-time updates on your delivery progress', icon: RefreshCw },
                { step: 3, title: 'Get WhatsApp Updates', description: 'Receive instant notifications when status changes', icon: MessageCircle }
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.step} className="text-center">
                    <div className="bg-[#fb6402] text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl mx-auto mb-4">
                      {item.step}
                    </div>
                    <Icon className="h-8 w-8 text-[#039fff] mx-auto mb-3" />
                    <h3 className="font-bold text-[#001A47] mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-600">{item.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Quick Links */}
      <section className="py-16 bg-[#001A47] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Common Questions</h2>
          <p className="text-gray-300 mb-8">Can&apos;t find your tracking info? We&apos;re here to help.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/support">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#001A47]">
                Support Center
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#001A47]">
                Contact Us
              </Button>
            </Link>
            <a href={`https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent('Hi Rocket Van Express, I need help tracking my delivery.')}`} target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#fb6402] hover:bg-[#e55a02]">
                <MessageCircle className="mr-2 h-4 w-4" />
                WhatsApp Support
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Track;
